import React, { useState, useEffect } from 'react';
import { Download, Search, Music, Play, Pause, FileAudio, Clock, AlertTriangle } from 'lucide-react';

const AudioTab = ({ routines, registrations, event, loading, downloadAllAudio, userDetails = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [audioPlayer, setAudioPlayer] = useState(null);
  
  // Debug logging
  useEffect(() => {
    console.log('AudioTab - User Details:', userDetails);
  }, [userDetails]);
  
  // Helper function to get user's full name
  const getUserFullName = (userId) => {
    if (!userId) return 'No User ID';
    
    // First try to find the registration for this userId
    const registration = registrations.find(reg => reg.userId === userId);
    if (registration && (registration.userFirstName || registration.userLastName)) {
      console.log(`Using user details from registration: ${registration.userFirstName} ${registration.userLastName}`);
      return `${registration.userFirstName || ''} ${registration.userLastName || ''}`.trim();
    }
    
    console.log(`Getting full name for userId: ${userId}`);
    console.log(`User details for ${userId}:`, userDetails[userId]);
    
    const user = userDetails[userId];
    if (!user) {
      console.log(`No user details found for userId: ${userId}`);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Check if user is a string (might be an error message)
    if (typeof user === 'string') {
      console.log(`User details for ${userId} is a string:`, user);
      return `Unknown User (${userId.substring(0, 8)}...)`;
    }
    
    // Try to extract name from different possible structures
    let firstName = '';
    let lastName = '';
    let email = '';
    
    // Direct properties
    if (user.firstName) firstName = user.firstName;
    if (user.lastName) lastName = user.lastName;
    if (user.email) email = user.email;
    
    // Nested properties
    if (user.user) {
      if (user.user.firstName) firstName = user.user.firstName;
      if (user.user.lastName) lastName = user.user.lastName;
      if (user.user.email) email = user.user.email;
    }
    
    // Attributes property (common in Cognito user objects)
    if (user.attributes) {
      if (user.attributes.given_name) firstName = user.attributes.given_name;
      if (user.attributes.family_name) lastName = user.attributes.family_name;
      if (user.attributes.email) email = user.attributes.email;
    }
    
    // Build the full name
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (email) {
      return email;
    }
    
    return ''; // Return empty string if no name or email found
  };
  
  // Filter routines based on search term and having audio
  const routinesWithAudio = routines.filter(routine => routine.audioUrl);
  const filteredRoutines = routinesWithAudio.filter(routine => {
    const searchLower = searchTerm.toLowerCase();
    const userFullName = getUserFullName(routine.userId).toLowerCase();
    
    return (
      (routine.name && routine.name.toLowerCase().includes(searchLower)) ||
      (routine.studioName && routine.studioName.toLowerCase().includes(searchLower)) ||
      (routine.category && routine.category.toLowerCase().includes(searchLower)) ||
      (routine.songFileName && routine.songFileName.toLowerCase().includes(searchLower)) ||
      userFullName.includes(searchLower)
    );
  });
  
  // Play/pause audio
  const toggleAudio = (routine) => {
    // If we're already playing this routine, pause it
    if (currentlyPlaying === routine.id) {
      audioPlayer.pause();
      setCurrentlyPlaying(null);
      setAudioPlayer(null);
      return;
    }
    
    // If we're playing something else, stop it
    if (audioPlayer) {
      audioPlayer.pause();
    }
    
    // Play the new audio
    const player = new Audio(routine.audioUrl);
    player.addEventListener('ended', () => {
      setCurrentlyPlaying(null);
      setAudioPlayer(null);
    });
    
    player.play();
    setCurrentlyPlaying(routine.id);
    setAudioPlayer(player);
  };
  
  // Format duration from seconds to MM:SS
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Download a single audio file
  const downloadAudio = (routine) => {
    if (!routine.audioUrl) return;
    
    const link = document.createElement('a');
    link.href = routine.audioUrl;
    // Use songFileName if available, otherwise create a name from routine details
    const fileName = routine.songFileName || 
      `${routine.studioName || 'Unknown'}_${routine.name || 'Untitled'}.mp3`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header with search and download all */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Search audio files..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
        </div>
        
        <button
          className="btn btn-primary btn-sm flex items-center gap-2"
          onClick={downloadAllAudio}
          disabled={!routinesWithAudio.length}
        >
          <Download className="h-4 w-4" />
          Download All Audio
        </button>
      </div>
      
      {/* Audio files count */}
      <div className="text-sm text-base-content/70">
        Showing {filteredRoutines.length} of {routinesWithAudio.length} audio files
        {routinesWithAudio.length < routines.length && (
          <span> ({routines.length - routinesWithAudio.length} routines have no audio)</span>
        )}
      </div>
      
      {/* Missing audio warning */}
      {routinesWithAudio.length < routines.length && (
        <div className="alert alert-warning">
          <AlertTriangle className="h-5 w-5" />
          <span>
            {routines.length - routinesWithAudio.length} routines are missing audio files.
            Please remind studios to upload their music.
          </span>
        </div>
      )}
      
      {/* Audio files list */}
      {filteredRoutines.length === 0 ? (
        <div className="text-center py-12 bg-base-200 rounded-lg">
          <p className="text-base-content/70">
            {routinesWithAudio.length === 0
              ? 'No audio files found for this event'
              : 'No audio files match your search'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead>
              <tr>
                <th>Routine Name</th>
                <th>Studio</th>
                <th>Category</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoutines.map((routine) => (
                <tr key={routine.id || `routine-${routine.name}`} className="hover">
                  <td className="font-medium">{routine.name || 'Untitled Routine'}</td>
                  <td>
                    <div>
                      <div>{routine.studioName || 'Unknown Studio'}</div>
                      {getUserFullName(routine.userId) && (
                        <div className="text-xs text-base-content/70">
                          {getUserFullName(routine.userId)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    {routine.category ? (
                      <span className="badge badge-outline">{routine.category}</span>
                    ) : (
                      <span className="text-base-content/50">-</span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{formatDuration(routine.duration)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button
                        className="btn btn-circle btn-sm btn-ghost"
                        onClick={() => toggleAudio(routine)}
                        title={currentlyPlaying === routine.id ? "Pause" : "Play"}
                      >
                        {currentlyPlaying === routine.id ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>
                      
                      <button
                        className="btn btn-circle btn-sm btn-ghost"
                        onClick={() => downloadAudio(routine)}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      
                      {routine.songFileName && (
                        <span className="text-xs text-base-content/70 ml-2">
                          {routine.songFileName}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AudioTab; 